import { cn } from "@/lib/utils";
import { Plus, Search, X } from "lucide-react";

import { Variant } from "./Types";

interface ButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant: Variant;
  large?: boolean;
  label?: string | React.ReactNode;
  icon?: React.ReactNode;
  active?: boolean;
  isMobileFilterButton?: boolean;
  disabled?: boolean;
}

const Button = ({
  label,
  variant,
  large,
  icon,
  onClick,
  active,
  isMobileFilterButton,
  disabled,
}: ButtonProps) => {
  if (isMobileFilterButton) {
    return <MobileFilterButton icon={icon} onClick={onClick} />;
  }

  return (
    <button
      className={cn("touch-device:p-0 touch-device:m-0", large && "w-full")}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      disabled={disabled}
    >
      <div
        className={cn(
          "flex flex-row px-2 h-9 gap-x-1 items-center rounded text-sm whitespace-nowrap justify-center group shadow-soft-dark-bottom",
          icon && !label && "w-9",
          large ? "touch-device:h-9" : "touch-device:h-7",
          variant === "primary" &&
            "bg-primary-action-default desktop:hover:bg-primary-action-hover text-white",
          variant === "secondary" &&
            (active
              ? "bg-action-secondary-active text-dark-primary"
              : "bg-action-secondary-default desktop:hover:bg-action-secondary-hover text-dark-primary hover:text-dark-primary"),
          variant === "tertiary" &&
            (active
              ? "bg-action-tertiary-active text-white"
              : "bg-action-tertiary text-white/90 hover:text-white desktop:hover:bg-action-tertiary-hover"),
          variant === "disabled" &&
            "bg-disabled text-dark-disabled cursor-default",
        )}
      >
        {label && label}
        {icon && <span>{icon}</span>}
      </div>
    </button>
  );
};

interface MobileFilterButtonProps {
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const MobileFilterButton = ({ icon, onClick }: MobileFilterButtonProps) => {
  return (
    <button
      className="p-2 -m-2 touch-device:p-0 touch-device:m-0"
      onClick={onClick}
    >
      <span className="flex h-9 flex-row px-2 py-1 -mx-1 -my-1 gap-x-1 items-center rounded text-sm whitespace-nowrap justify-center group text-gray-300">
        {icon}
      </span>
    </button>
  );
};

interface SearchButtonProps extends ButtonProps {
  isExpanded: boolean;
  searchQuery?: string;
  onClose: () => void;
  onChange: (value: string) => void;
}

export const SearchButton = ({
  isExpanded,
  variant,
  label,
  onClick,
  searchQuery,
  onChange,
  onClose,
}: SearchButtonProps) => {
  return (
    <>
      {isExpanded ? (
        <div className="flex flex-row items-center justify-center bg-new-gray-650 px-2 py-2 cursor-pointer shadow-lg rounded-lg">
          <Search className="h-5 w-5 text-gray-400 mr-2 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="Add Player"
            className="bg-new-gray-650 outline-none w-full placeholder:text-gray-400 h-full text-base mobile:text-sm"
            autoFocus
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-400 ml-2 hover:text-gray-400 transition-colors shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <Button
          label={label}
          variant={variant}
          icon={<Plus className="h-5 w-5" />}
          onClick={onClick}
        />
      )}
    </>
  );
};

export default Button;
